import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { CheckCircle2, MapPin, Hash, Loader2, ArrowRight, Phone } from 'lucide-react'
import { supabase } from '@/config/supabase'
import useAuthStore from '@/stores/authStore'
import { ROUTES } from '@/config/routes'
import Button from '@/components/ui/Button'

export default function BookingConfirmed() {
  const { id: bookingId } = useParams()
  const navigate = useNavigate()
  const profile = useAuthStore((s) => s.profile)

  const [booking, setBooking] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!bookingId || !profile?.id) return
    supabase
      .from('bookings')
      .select('id, ref_number, status, address, created_at, service_categories ( name )')
      .eq('id', bookingId)
      .eq('customer_id', profile.id)
      .single()
      .then(({ data }) => {
        setBooking(data)
        setLoading(false)
      })
  }, [bookingId, profile?.id])

  if (loading) {
    return (
      <div className="min-h-dvh bg-surface-base flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-siglat-teal animate-spin" />
      </div>
    )
  }

  return (
    <div className="min-h-dvh bg-surface-base flex flex-col items-center justify-center px-4 py-12">

      {/* Background accent */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden" aria-hidden>
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-siglat-teal/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-siglat-teal/3 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-sm flex flex-col gap-6 animate-fade-in">

        {/* Success icon */}
        <div className="flex flex-col items-center gap-3 text-center">
          <div className="w-16 h-16 rounded-full bg-green-500/10 border border-green-500/25 flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8 text-green-400" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold text-ink-primary">Booking Confirmed!</h1>
            <p className="text-sm text-ink-muted mt-1">
              Your payment has been verified. We're on it.
            </p>
          </div>
        </div>

        {/* Booking details card */}
        {booking && (
          <div className="bg-surface-raised border border-surface-border rounded-xl p-5 flex flex-col gap-3">
            {booking.ref_number && (
              <div className="flex items-center gap-2.5">
                <Hash className="w-4 h-4 text-ink-muted shrink-0" />
                <div>
                  <p className="text-[10px] text-ink-muted uppercase tracking-wider font-semibold">Booking Ref</p>
                  <p className="text-sm font-mono font-semibold text-ink-primary">{booking.ref_number}</p>
                </div>
              </div>
            )}
            {booking.service_categories?.name && (
              <div className="flex items-center gap-2.5">
                <div className="w-4 h-4 shrink-0 flex items-center justify-center">
                  <span className="text-xs text-ink-muted">🔧</span>
                </div>
                <div>
                  <p className="text-[10px] text-ink-muted uppercase tracking-wider font-semibold">Service</p>
                  <p className="text-sm text-ink-primary">{booking.service_categories.name}</p>
                </div>
              </div>
            )}
            {booking.address && (
              <div className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 text-ink-muted shrink-0 mt-0.5" />
                <div>
                  <p className="text-[10px] text-ink-muted uppercase tracking-wider font-semibold">Location</p>
                  <p className="text-sm text-ink-primary">{booking.address}</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* What happens next */}
        <div className="bg-surface-raised border border-surface-border rounded-xl p-5">
          <p className="text-xs font-semibold text-ink-secondary uppercase tracking-wider mb-3">What happens next</p>
          <div className="flex flex-col gap-3">
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 rounded-full bg-siglat-teal/15 border border-siglat-teal/30 flex items-center justify-center shrink-0 mt-0.5">
                <span className="text-[10px] font-bold text-siglat-teal">1</span>
              </div>
              <p className="text-sm text-ink-muted">Our team will contact you within <span className="text-ink-primary font-medium">24 hours</span> to confirm the schedule.</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 rounded-full bg-siglat-teal/15 border border-siglat-teal/30 flex items-center justify-center shrink-0 mt-0.5">
                <span className="text-[10px] font-bold text-siglat-teal">2</span>
              </div>
              <p className="text-sm text-ink-muted">A verified worker will be dispatched to your location.</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-5 h-5 rounded-full bg-siglat-teal/15 border border-siglat-teal/30 flex items-center justify-center shrink-0 mt-0.5">
                <span className="text-[10px] font-bold text-siglat-teal">3</span>
              </div>
              <p className="text-sm text-ink-muted">Job completion is documented with photos and a <span className="text-ink-primary font-medium">30-day warranty</span>.</p>
            </div>
          </div>
        </div>

        {/* Contact note */}
        <div className="flex items-center gap-2 bg-siglat-teal/5 border border-siglat-teal/20 rounded-lg px-4 py-3">
          <Phone className="w-3.5 h-3.5 text-siglat-teal shrink-0" />
          <p className="text-xs text-ink-muted">
            Urgent? Call us at{' '}
            <a href="tel:+639456086917" className="text-siglat-teal font-semibold">+63 945 608 6916</a>
          </p>
        </div>

        {/* CTA */}
        <Button
          fullWidth
          size="lg"
          onClick={() => navigate(ROUTES.BOOK_HISTORY)}
          rightIcon={<ArrowRight className="w-4 h-4" />}
        >
          View My Projects
        </Button>

      </div>
    </div>
  )
}
